import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAdminRequest,
  fetchAdminsRequest,
  toggleAdminStatusRequest,
} from "../../../redux/AdminList/adminSlice";
import { createReferralRedeemRequest } from "../../../redux/Order/RedeemOrdersSlice";
import {
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
} from "@mui/material";
import { CiEdit } from "react-icons/ci";
import { ImEye } from "react-icons/im";
import { RiDeleteBin6Line } from "react-icons/ri";
import TableCompo from "../Table/TableCompo";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";
import { canShowPermission } from "../Permissions/permissionObject";

const DeleteToast = ({ adminId, closeToast, dispatch }) => {
  const [yesLoading, setYesLoading] = useState(false);
  const [noLoading, setNoLoading] = useState(false);

  const handleYes = () => {
    setYesLoading(true);
    setTimeout(() => {
      dispatch(deleteAdminRequest(adminId));
      toast.success("Admin deleted successfully!");
      closeToast();
    }, 2000);
  };

  const handleNo = () => {
    setNoLoading(true);
    setTimeout(() => {
      closeToast();
      toast.error("Delete cancelled");
    }, 2000);
  };

  return (
    <div
      style={{
        background: "#fff",
        padding: "16px 20px",
        borderRadius: "8px",
        boxShadow: "0px 2px 10px rgba(0,0,0,0.4)",
        color: "#000",
        fontSize: "14px",
        maxWidth: "300px",
      }}
    >
      <p>Are you sure you want to delete this admin?</p>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginTop: "10px",
        }}
      >
        <button
          style={{
            background: "#8e5addff",
            color: "#fff",
            border: "none",
            padding: "6px 12px",
            borderRadius: "4px",
            cursor: "pointer",
            minWidth: "60px",
          }}
          disabled={yesLoading || noLoading}
          onClick={handleYes}
        >
          {yesLoading ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            "Yes"
          )}
        </button>

        <button
          style={{
            background: "#9e9e9e",
            color: "#fff",
            border: "none",
            padding: "6px 12px",
            borderRadius: "4px",
            cursor: "pointer",
            minWidth: "60px",
          }}
          disabled={yesLoading || noLoading}
          onClick={handleNo}
        >
          {noLoading ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            "No"
          )}
        </button>
      </div>
    </div>
  );
};

const AdminList = ({ refresh, onEdit }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { convertToCsv } = useCsvConverter();
  const {
    admins = [],
    loading,
    total = 0,
  } = useSelector((state) => state.admin || {});

  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [searchText, setSearchText] = useState("");
  const [permissions, setPermissions] = useState({});
  const [confirmStatusDialogOpen, setConfirmStatusDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [statusLoadingYes, setStatusLoadingYes] = useState(false);
  const [statusLoadingNo, setStatusLoadingNo] = useState(false);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redeemConfirmDialogOpen, setRedeemConfirmDialogOpen] = useState(false);
  const [selectedPointRow, setSelectedPointRow] = useState(null);
  const [redeemData, setRedeemData] = useState({ field1: "", field2: "" });

  const handlePointClick = (row) => {
    const pointValue = row?.referralpoint?.points ?? 0;
    if (pointValue <= 0) {
      toast.warning("No points available to redeem!");
      return;
    }

    setSelectedPointRow({
      ...row,
      userId: row._id,
      referralpointId: row?.referralpoint?._id || null,
      referralPoints: row?.referralpoint?.points ?? 0,
    });
    setRedeemDialogOpen(true);
  };

  const handleRedeemYes = () => {
    setRedeemDialogOpen(false);
    setRedeemData({
      field1: selectedPointRow?.field1 || "",
      field2: selectedPointRow?.referralPoints || 0,
      referralpointId: selectedPointRow?.referralpointId || "",
    });
    setRedeemConfirmDialogOpen(true);
  };

  const handleRedeemNo = () => {
    setRedeemDialogOpen(false);
    setSelectedPointRow(null);
  };

  const handleSaveRedeem = () => {
    if (!redeemData.field2 || !redeemData.field1) {
      toast.error("Please enter both Points and Branch Code!");
      return;
    }

    const payload = {
      Id: selectedPointRow?.referralpointId,
      pointsToRedeem: Number(redeemData.field2),
      branchcode: redeemData.field1,
      userId: selectedPointRow?.userId,
    };

    dispatch(createReferralRedeemRequest(payload));
    setRedeemConfirmDialogOpen(false);
    setSelectedPointRow(null);
  };

  const handleCancelRedeem = () => {
    setRedeemConfirmDialogOpen(false);
    setSelectedPointRow(null);
  };
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);

  const canEdit = canShowPermission(permissions, "admins", "edit");
  const canDelete = canShowPermission(permissions, "admins", "delete");

  useEffect(() => {
    const start = pagination.page * pagination.limit;
    dispatch(
      fetchAdminsRequest({
        start,
        limit: pagination.limit,
        search: searchText,
      }),
    );
  }, [dispatch, refresh, pagination, searchText]);

  const handleTableChange = (action, tableState) => {
    if (action === "changePage") {
      setPagination((prev) => ({ ...prev, page: tableState.page }));
    } else if (action === "changeRowsPerPage") {
      setPagination({ page: 0, limit: tableState.rowsPerPage });
    } else if (action === "search") {
      setSearchText(tableState.searchText || "");
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleStatusChange = (admin) => {
    setSelectedAdmin(admin);
    setConfirmStatusDialogOpen(true);
  };

  const handleConfirmStatusYes = () => {
    if (!selectedAdmin) return;
    setStatusLoadingYes(true);
    const updatedStatus = !selectedAdmin.enabled;
    dispatch(
      toggleAdminStatusRequest({
        userId: selectedAdmin._id,
        enabled: updatedStatus,
      }),
    );
    setTimeout(() => {
      toast.success(
        `Admin ${updatedStatus ? "enabled" : "disabled"} successfully`,
      );
      setStatusLoadingYes(false);
      setConfirmStatusDialogOpen(false);
      setSelectedAdmin(null);
    }, 2000);
  };

  const handleConfirmStatusNo = () => {
    setStatusLoadingNo(true);
    setTimeout(() => {
      setStatusLoadingNo(false);
      setConfirmStatusDialogOpen(false);
      setSelectedAdmin(null);
      toast.error("Status change cancelled");
    }, 1000);
  };

  const handleDeleteClick = (adminId) => {
    const currentUserEmail = localStorage.getItem("userEmail");
    const admin = admins.find((a) => a._id === adminId);
    if (admin?.email === currentUserEmail) {
      toast.error("You cannot delete your own admin account.");
      return;
    }

    toast(
      (t) => (
        <DeleteToast
          adminId={adminId}
          closeToast={() => toast.dismiss(t.id)}
          dispatch={dispatch}
        />
      ),
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        style: { padding: 0, background: "none", boxShadow: "none" },
      },
    );
  };

  const columns = [
    { field: "id", headerName: "ID", hide: true, hideInExcel: true },
    { field: "name", headerName: "Name" },
    {
      field: "email",
      headerName: "Email",
      minWidth: 250,
      render: (_, row) => (
        <Box
          sx={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: 250 }}
        >
          {row.email}
        </Box>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      minWidth: 100,
      render: (_, row) => (
        <Box
          sx={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: 100 }}
        >
          {row.phone}
        </Box>
      ),
    },
    {
      field: "roleName",
      headerName: "Role",
      minWidth: 100,
      render: (_, row) => (
        <Box
          sx={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: 100 }}
        >
          {row?.userRole?.role && row.userRole.role.trim() !== ""
            ? row.userRole.role
            : "N.A"}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      render: (_, row) =>
        canEdit ? (
          <Button
            variant="contained"
            style={{
              backgroundColor: row.enabled ? "green" : "#d32f2f",
              color: "white",
              textTransform: "none",
            }}
            onClick={() => handleStatusChange(row)}
          >
            {row.enabled ? "Enable" : "Disable"}
          </Button>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: row.enabled ? "green" : "#d32f2f",
              fontWeight: 600,
            }}
          >
            {row.enabled ? "Enable" : "Disable"}
          </Typography>
        ),
      hideInExcel: true,
    },
    ...(canEdit
      ? [
          {
            field: "edit",
            headerName: "Edit",
            minWidth: 50,
            render: (_, row) => (
              <CiEdit
                style={{ cursor: "pointer", fontSize: "16px" }}
                onClick={() => onEdit?.(row)}
              />
            ),
            hideInExcel: true,
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            field: "delete",
            headerName: "Delete",
            minWidth: 80,
            render: (_, row) => (
              <RiDeleteBin6Line
                style={{ cursor: "pointer", color: "red", fontSize: "16px" }}
                onClick={() => handleDeleteClick(row._id)}
              />
            ),
            hideInExcel: true,
          },
        ]
      : []),
    {
      field: "referralcode",
      headerName: "Referral Code",
      render: (val, row) =>
        row?.referralcode ? (
          <button
            style={{
              background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
              fontFamily: "inherit",
              color: "#fff",
              border: "none",
              padding: "5px 12px",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
            onClick={() => {
              sessionStorage.setItem("selectedReferralCode", row.referralcode);
              window.location.href = "/BirthdayCustomerList";
            }}
          >
            {row.referralcode}
          </button>
        ) : (
          "N.A"
        ),
    },
    {
      field: "referralpoint",
      headerName: "Points",
      minWidth: 100,
      render: (_, row) => {
        const pointValue = row?.referralpoint?.points ?? 0;

        return (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => canEdit && handlePointClick(row)}
          >
            {pointValue}
          </Button>
        );
      },
    },
    {
      field: "createdBY",
      headerName: "Created By",
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.createdBy ? row.createdBy.name : "N/A";
      },
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.updatedBy ? row.updatedBy.name : "N/A";
      },
    },
  ];

  const requestCsvDatarequestCsvData = () => {
    console.log("requestCsvDatarequestCsvData", "called");
    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/users/adminsCsv",
        query: {},
        callback: (response) => {
          console.log("Report data:", response);

          const csv = convertToCsv(response?.data || []);
          console.log("csv", csv);
          triggerCsvDownload(csv, "report.csv");
        },
      }),
    );
  };

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableCompo
          columns={columns}
          data={admins
            .filter((a) => a?.permissions?.role?.toLowerCase() !== "god")}
          loading={loading}
          totalCount={total}
          page={pagination.page}
          limit={pagination.limit}
          onTableChange={handleTableChange}
          extraButtons={
            <Button
              size="sm"
              variant="outlined"
              onClick={requestCsvDatarequestCsvData}
            >
              Download CSV
            </Button>
          }
        />
      )}

      <Dialog open={confirmStatusDialogOpen} onClose={handleConfirmStatusNo}>
        <DialogTitle>Change Status</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to{" "}
            {selectedAdmin?.enabled ? "disable" : "enable"} this admin?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmStatusYes}
            disabled={statusLoadingYes}
          >
            {statusLoadingYes ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Yes"
            )}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmStatusNo}
            disabled={statusLoadingNo}
          >
            {statusLoadingNo ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "No"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={redeemDialogOpen} onClose={handleRedeemNo}>
        <DialogTitle>Redeem Points</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to redeem <b>{selectedPointRow?.referralPoints}</b>{" "}
            points for <b>{selectedPointRow?.name}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRedeemYes} color="success" variant="contained">
            Yes
          </Button>
          <Button onClick={handleRedeemNo} color="error" variant="contained">
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/*  Second Dialog: Show selected row details */}
      <Dialog open={redeemConfirmDialogOpen} onClose={handleCancelRedeem}>
        <DialogTitle>Redeem Point Details</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          <TextField
            label="Points to Redeem"
            type="number"
            value={redeemData.field2}
            onChange={(e) => {
              const value = Number(e.target.value);
              const maxPoints = selectedPointRow?.referralPoints || 0;
              if (e.target.value === "") {
                setRedeemData({ ...redeemData, field2: "" });
                return;
              }
              if (isNaN(value) || value <= 0) {
                setRedeemData({ ...redeemData, field2: e.target.value });
                return;
              }
              if (value > maxPoints) {
                toast.error(`You can redeem maximum ${maxPoints} points only`);
                return;
              }

              setRedeemData({ ...redeemData, field2: value });
            }}
            fullWidth
            error={
              redeemData.field2 !== "" &&
              (isNaN(redeemData.field2) || Number(redeemData.field2) <= 0)
            }
            helperText={
              redeemData.field2 !== "" &&
              (isNaN(redeemData.field2) || Number(redeemData.field2) <= 0)
                ? "Please enter a positive number"
                : ""
            }
          />
          <TextField
            label="Branch Code"
            value={redeemData.field1}
            onChange={(e) =>
              setRedeemData({ ...redeemData, field1: e.target.value })
            }
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSaveRedeem}
            variant="contained"
            color="success"
          >
            Create Order
          </Button>
          <Button
            onClick={handleCancelRedeem}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminList;
